import React from 'react';
import initApollo from './init-apollo';
import Head from 'next/head';
import { getDataFromTree } from 'react-apollo';
import PropTypes from 'prop-types';

function withApolloClient({ App, schema }) {
  const propTypes = {
    apolloClient: PropTypes.any,
    apolloState: PropTypes.any,
    response: PropTypes.func,
  };

  const defaultProps = {
    response: () => null,
  };

  const Apollo = ({ apolloClient, apolloState, response, ...props }) => {
    // `getDataFromTree` renders the component first, the client is passed off as a property.
    // After that rendering is done using Next's normal rendering pipeline
    return (
      <App
        {...props}
        apolloClient={apolloClient || initApollo(apolloState.data, schema)}
      />
    );
  };

  Apollo.getInitialProps = async context => {
    // ctx contains the request context if we're on the server
    const { Component, router, ctx } = context;

    let props = App.getInitialProps ? await App.getInitialProps(context) : {};

    // Run all GraphQL queries in the component tree and extract the resulting data
    const apolloClient = initApollo({}, schema, ctx);

    if (!process.browser) {
      try {
        // Run all GraphQL queries
        await getDataFromTree(
          <App
            {...props}
            Component={Component}
            router={router}
            apolloClient={apolloClient}
          />
        );
      } catch (error) {
        // Prevent Apollo Client GraphQL errors from crashing SSR.
        // Handle them in components via the data.error prop:
        // https://www.apollographql.com/docs/react/basics/queries.html#graphql-query-data-error
      }

      // getDataFromTree does not call componentWillUnmount
      // head side effect therefore need to be cleared manually
      Head.rewind();
    }

    return {
      ...props,
      // The Server State
      apolloState: {
        // Extract query data from the Apollo store
        data: apolloClient.cache.extract(),
      },
      // https://github.com/zeit/next.js/issues/4452#issuecomment-469390375
      response: () => ctx.res,
    };
  };

  Apollo.displayName = 'withApolloClient(App)';
  Apollo.propTypes = propTypes;
  Apollo.defaultProps = defaultProps;

  return Apollo;
}

export default withApolloClient;
